'use client';

import { faArrowLeft, faSearch, faTimes } from '@fortawesome/pro-light-svg-icons';
import { Activity, useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { searchHealthResources } from '@/app/(main)/_components/search-modal/_services/search-health-resources.service';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { SheetClose, SheetHeader } from '@/components/ui/sheet';
import { MostSearched } from '@/app/(main)/_components/search-modal/_components/most-searched.component';
import {
  clearSearchState,
  removeSelectedSearch,
  setFetchedResults,
  setSearchTerm,
  useSearchState,
} from '@/store/search/search.store';
import { AllSearchResults, SearchType } from '@/app/(main)/_components/search-modal/search-modal.types';
import { useDebounceFn } from '@/hooks/use-debounce.hook';
import { searchHealthResourcesByType } from '@/app/(main)/_components/search-modal/_services/search-health-resources-by-type.service';
import { SearchFilters } from '@/app/(main)/_components/search-modal/_components/search-filters.component';
import { SearchSkeletonLoading } from '@/app/(main)/_components/search-modal/_components/loadings/search-skeleton-loading.component';
import { RecentSearched } from '@/app/(main)/_components/search-modal/_components/recent-searched.component';
import { SearchEmptyState } from '@/app/(main)/_components/search-modal/_components/search-empty-state.component';
import { SelectedProcedures } from '@/app/(main)/(pages)/_components/search/search.component';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

type SearchModalProps = {
  selectLaboratoryProcedure?: (procedure: SelectedProcedures) => void;
  setOpen: (open: boolean) => void;
  locationOverride?: OverridedUserLocation;
};

const placeholders = [
  'O que você busca?',
  'Busque pela especialidade',
  'Busque pelo exame',
  'Busque pelo nome do médico',
  'Busque pelo nome do profissional',
  'Busque pelo laboratório',
];

export const SearchModalDefault = ({ selectLaboratoryProcedure, setOpen, locationOverride }: SearchModalProps) => {
  const searchState = useStore(useSearchState, (state) => state);
  const analytics = getAnalyticsPlugin();

  const chooseInitialResults = () => {
    const { fetchedResults } = searchState;

    if (fetchedResults) {
      return fetchedResults;
    }

    return {
      [SearchType.ALL]: {
        results: [],
        total: 0,
      },
      [SearchType.PROCEDURE]: {
        results: [],
        total: 0,
      },
      [SearchType.PROFESSIONAL]: {
        results: [],
        total: 0,
      },
      [SearchType.PARTNER]: {
        results: [],
        total: 0,
      },
    };
  };

  const chooseShouldShowSearchResults = () => {
    const { fetchedResults } = searchState;

    if (fetchedResults) {
      return Object.values(fetchedResults).some((result) => result.results.length > 0);
    }

    return false;
  };

  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);

  const [inputValue, setInputValue] = useState('');
  const [searchDisplayValue, setSearchDisplayValue] = useState('');

  useEffect(() => {
    if (searchState?.searchTerm) {
      setInputValue(searchState.searchTerm);
      setSearchDisplayValue(searchState.searchTerm);
    }
  }, [searchState.searchTerm]);

  const [shouldShowSearchResults, setShouldShowSearchResults] = useState(chooseShouldShowSearchResults());
  const [shouldShowEmptyState, setShouldShowEmptyState] = useState(false);

  const [searchResultIsLoading, setSearchResultIsLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const headerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      headerRef.current = node;
      setHeaderHeight(node.offsetHeight);
    }
  }, []);

  const [allResults, setAllResults] = useState<AllSearchResults>(chooseInitialResults());

  const activeRequestId = useRef(0);

  const debouncedChange = useDebounceFn(setInputValue, 400);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const { scrollTop } = container;
          setIsScrolled(scrollTop > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize ?? (entry.target as HTMLElement).offsetHeight;
        setHeaderHeight(height);
      }
    });

    resizeObserver.observe(header);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleBackClick = () => {
    setOpen(false);
  };

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedChange(e.target.value);
    setSearchDisplayValue(e.target.value);
  };

  useEffect(() => {
    const currentRequestId = ++activeRequestId.current;

    if (inputValue.length < 3) {
      clearSearchState();
      setAllResults({
        [SearchType.ALL]: {
          results: [],
          total: 0,
        },
        [SearchType.PROCEDURE]: {
          results: [],
          total: 0,
        },
        [SearchType.PROFESSIONAL]: {
          results: [],
          total: 0,
        },
        [SearchType.PARTNER]: {
          results: [],
          total: 0,
        },
      });
      setShouldShowSearchResults(false);
      setShouldShowEmptyState(false);
      setSearchResultIsLoading(false);
      return;
    }

    const oldSearchTerm = searchState.searchTerm;

    if (oldSearchTerm === inputValue) {
      return;
    }

    async function fetchResults() {
      setAllResults({
        [SearchType.ALL]: {
          results: [],
          total: 0,
        },
        [SearchType.PROCEDURE]: {
          results: [],
          total: 0,
        },
        [SearchType.PROFESSIONAL]: {
          results: [],
          total: 0,
        },
        [SearchType.PARTNER]: {
          results: [],
          total: 0,
        },
      });

      if (shouldShowSearchResults) {
        setShouldShowSearchResults(false);
      }

      if (shouldShowEmptyState) {
        setShouldShowEmptyState(false);
      }

      setSearchResultIsLoading(true);
      try {
        const all = searchHealthResources(inputValue);
        const procedures = searchHealthResourcesByType(inputValue, SearchType.PROCEDURE);
        const professionals = searchHealthResourcesByType(inputValue, SearchType.PROFESSIONAL);
        const partners = searchHealthResourcesByType(inputValue, SearchType.PARTNER);

        const results = {
          [SearchType.ALL]: await all,
          [SearchType.PROCEDURE]: await procedures,
          [SearchType.PROFESSIONAL]: await professionals,
          [SearchType.PARTNER]: await partners,
        };

        if (currentRequestId === activeRequestId.current) {
          const hasAnyResult = Object.values(results).some((result) => result.total > 0);

          if (hasAnyResult) {
            setShouldShowSearchResults(true);
            setShouldShowEmptyState(false);
          } else {
            setShouldShowEmptyState(true);
          }

          setFetchedResults(results);
          setSearchTerm(inputValue);
          setAllResults(results);
        }
      } catch (error) {
        const logger = getLogger('SearchModal - Default');
        logger.error('Error fetching search results', { inputValue, currentRequestId, error });
        if (currentRequestId === activeRequestId.current) {
          setShouldShowEmptyState(true);
        }
      } finally {
        if (currentRequestId === activeRequestId.current) {
          setSearchResultIsLoading(false);
        }
      }
    }

    fetchResults();
  }, [inputValue]);

  function handleRemoveSelectedSearch() {
    removeSelectedSearch();
    analytics.track(AnalyticsEnum.CLICK_CLEAR_FILTERS_SEARCH_MODAL);
  }

  return (
    <div ref={containerRef} className="flex flex-col h-full overflow-y-auto" data-testid={DataTestIdEnum.PROCEDURES_MODAL_CONTAINER}>
      <SheetHeader ref={headerCallbackRef} className={`sticky top-0 left-0 right-0 z-20 px-5 pt-5 pb-3 transition-colors duration-200 ${isScrolled ? 'bg-gray-50' : 'bg-white'} ${shouldShowSearchResults && !shouldShowEmptyState ? '' : 'shadow-floating'}`}>
        <div className="md:flex justify-end gap-3 items-center hidden md:visible">
          <SheetClose asChild className="text-gray-500 cursor-pointer self-center hover:bg-gray-50">
            <Icon icon={faTimes} size="xl" className="hover:text-gray-900 text-[1.625rem]" />
          </SheetClose>
        </div>
        <div className="mb-4 pt-2 flex gap-4">
          <div className="md:hidden flex justify-center align-middle">
            <Icon
              icon={faArrowLeft}
              size="xl"
              className="text-gray-500 self-center cursor-pointer"
              onClick={handleBackClick}
            />
          </div>
          <Input
            placeholder={placeholders[currentPlaceholderIndex]}
            startIcon={faSearch}
            startIconStyle="text-green-500 text-xl"
            value={searchDisplayValue}
            onChange={handleSearchInput}
            onClear={handleRemoveSelectedSearch}
            data-testid={DataTestIdEnum.PROCEDURES_MODAL_INPUT}
            cleanValueDataTestId={DataTestIdEnum.PROCEDURES_MODAL_INPUT_CLEAR}
            ref={inputRef}
          />
        </div>
      </SheetHeader>
      {!shouldShowEmptyState && !shouldShowSearchResults && !searchResultIsLoading && (
        <div className="flex flex-col gap-3 px-5 pb-5">
          <RecentSearched
            setOpen={setOpen}
            selectLaboratoryProcedure={selectLaboratoryProcedure}
            locationOverride={locationOverride}
          />
          <MostSearched
            setOpen={setOpen}
            selectLaboratoryProcedure={selectLaboratoryProcedure}
            locationOverride={locationOverride}
          />
        </div>
      )}
      {shouldShowEmptyState && <div className="px-5 pb-5"><SearchEmptyState setOpen={setOpen} searchTerm={inputValue} /></div>}
      {shouldShowSearchResults && !shouldShowEmptyState && (
        <SearchFilters
          results={allResults}
          searchTerm={inputValue}
          setOpen={setOpen}
          selectLaboratoryProcedure={selectLaboratoryProcedure}
          locationOverride={locationOverride}
          isScrolled={isScrolled}
          headerHeight={headerHeight}
        />
      )}
      <Activity mode={searchResultIsLoading ? 'visible' : 'hidden'}>
        <div className="px-5 pb-5">
          <SearchSkeletonLoading />
        </div>
      </Activity>

    </div>
  );
};
